import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@geely/ui";
import { Calendar, Clock, Mail, Phone, User, Hash, IdCard, Car, Gauge, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatDate, getDayOfWeekLabel, getStatusLabel, getStatusColor } from '@/lib/utils';
import type { Appointment } from '@geely/shared';

interface AppointmentDetailDialogProps {
  appointment: Appointment | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AppointmentDetailDialog({ appointment, isOpen, onClose }: AppointmentDetailDialogProps) {
  if (!appointment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle>Detalhes do Agendamento</DialogTitle>
            <Badge
              variant="outline"
              className={`text-sm px-3 py-1 ${getStatusColor(appointment.status)}`}
            >
              {getStatusLabel(appointment.status)}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4 max-h-[calc(95vh-10rem)] overflow-y-auto pr-2">
          {/* Data e Horário */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Calendar className="size-3.5" />
                Data
              </div>
              <p className="text-sm font-medium">
                {formatDate(appointment.date)}
              </p>
              <p className="text-xs text-gray-500">
                {getDayOfWeekLabel(appointment.date)}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Clock className="size-3.5" />
                Horário
              </div>
              <p className="text-sm font-medium">{appointment.time}</p>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Status de Confirmação */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
              {appointment.confirmedAt ? (
                <CheckCircle2 className="size-3.5 text-green-600" />
              ) : (
                <AlertCircle className="size-3.5 text-yellow-600" />
              )}
              Confirmação de Presença
            </div>
            {appointment.confirmedAt ? (
              <div className="space-y-0.5">
                <p className="text-sm font-medium text-green-700">Confirmado</p>
                <p className="text-xs text-gray-500">
                  {new Date(appointment.confirmedAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            ) : (
              <p className="text-sm text-yellow-700">Aguardando confirmação</p>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* Dados do cliente */}
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <User className="size-3.5" />
                Cliente
              </div>
              <p className="text-sm font-medium">{appointment.customerName}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Phone className="size-3.5" />
                Telefone
              </div>
              <p className="text-sm">{appointment.customerPhone}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Mail className="size-3.5" />
                Email
              </div>
              <p className="text-sm">{appointment.customerEmail}</p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <IdCard className="size-3.5" />
                CPF
              </div>
              <p className="text-sm">{appointment.customerCpf}</p>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Dados do veículo */}
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Car className="size-3.5" />
                Modelo / Versão
              </div>
              <p className="text-sm font-medium">
                {appointment.model?.name ?? '-'} / {appointment.version?.name ?? '-'}
              </p>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Car className="size-3.5" />
                Placa
              </div>
              <p className="text-sm">{appointment.vehiclePlate}</p>
            </div>

            {appointment.vehicleVin && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                  <Car className="size-3.5" />
                  Chassi (VIN)
                </div>
                <p className="text-sm font-mono">{appointment.vehicleVin}</p>
              </div>
            )}

            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
                <Gauge className="size-3.5" />
                Quilometragem
              </div>
              <p className="text-sm">{appointment.vehicleMileage?.toLocaleString('pt-BR')} km</p>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* ID */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium uppercase">
              <Hash className="size-3.5" />
              ID do Agendamento
            </div>
            <p className="text-xs font-mono text-gray-600 bg-gray-50 rounded px-2 py-1 break-all">
              {appointment.id}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
